import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ChartBarIcon,
  UserGroupIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

interface SurveyQuestion {
  id: number;
  frage_text: string;
  frage_typ: 'single' | 'multiple' | 'freitext';
  optionen: Array<{ id: string; text: string }>;
}

interface RecipientAnswer {
  empfaenger_id: number;
  empfaenger_name: string;
  empfaenger_email: string;
  schueler_vorname: string;
  schueler_name: string;
  schueler_klasse: string;
  beantwortet_am: string;
  antworten: {
    [frageId: string]: {
      ausgewaehlte_optionen: string[];
      antwort_text?: string;
    };
  };
}

interface SurveyResponseDetailsProps {
  elternbriefId: number;
  modulId: number;
  modulTitel: string;
  isOpen: boolean;
  onClose: () => void;
}

const SurveyResponseDetails: React.FC<SurveyResponseDetailsProps> = ({
  elternbriefId,
  modulId,
  modulTitel,
  isOpen,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [responses, setResponses] = useState<RecipientAnswer[]>([]);
  const [filteredResponses, setFilteredResponses] = useState<RecipientAnswer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [classes, setClasses] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadSurveyDetails();
    }
  }, [isOpen, modulId]);

  useEffect(() => {
    filterResponses();
  }, [responses, searchTerm, filterClass]);

  const loadSurveyDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/massenmail/${elternbriefId}/survey/${modulId}/details`);
      if (response.data.success) {
        setQuestions(response.data.data.fragen);
        setResponses(response.data.data.antworten);
        
        // Extract unique classes
        const uniqueClasses = [...new Set(response.data.data.antworten.map((r: RecipientAnswer) => r.schueler_klasse))].sort() as string[];
        setClasses(uniqueClasses);
      }
    } catch (error: any) {
      console.error('Error loading survey details:', error);
      toast.error('Fehler beim Laden der Umfrage-Details');
    } finally {
      setLoading(false);
    }
  };

  const filterResponses = () => {
    let filtered = [...responses];

    // Filter by class
    if (filterClass) {
      filtered = filtered.filter(r => r.schueler_klasse === filterClass);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.schueler_vorname.toLowerCase().includes(term) ||
        r.schueler_name.toLowerCase().includes(term) ||
        r.empfaenger_name.toLowerCase().includes(term) ||
        r.schueler_klasse.toLowerCase().includes(term)
      );
    }

    setFilteredResponses(filtered);
  };

  const getAnswerText = (recipientAnswer: RecipientAnswer, question: SurveyQuestion): string => {
    const answer = recipientAnswer.antworten[question.id];
    if (!answer) return '-';

    if (question.frage_typ === 'freitext') {
      return answer.antwort_text || '-';
    }

    if (answer.ausgewaehlte_optionen && answer.ausgewaehlte_optionen.length > 0) {
      return answer.ausgewaehlte_optionen
        .map(optId => {
          const option = question.optionen.find(o => o.id === optId);
          return option ? option.text : optId;
        })
        .join(', ');
    }

    return '-';
  };

  const handleExportCSV = () => {
    // Build CSV content
    const headers = [
      'Schüler',
      'Klasse',
      'Empfänger',
      'Email',
      'Beantwortet am',
      ...questions.map(q => q.frage_text)
    ];

    const rows = filteredResponses.map(recipient => [
      `${recipient.schueler_vorname} ${recipient.schueler_name}`,
      recipient.schueler_klasse,
      recipient.empfaenger_name,
      recipient.empfaenger_email,
      new Date(recipient.beantwortet_am).toLocaleString('de-DE'),
      ...questions.map(q => getAnswerText(recipient, q))
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Download CSV
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `umfrage_${modulTitel.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    toast.success('CSV-Datei wurde heruntergeladen');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <ChartBarIcon className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Umfrage-Antworten: {modulTitel}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {filteredResponses.length} von {responses.length} Antworten
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Filters and Export */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Klasse filtern
                </label>
                <select
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Alle Klassen</option>
                  {classes.map((className) => (
                    <option key={className} value={className}>
                      {className}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Suchen
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nach Name oder Klasse suchen..."
                  />
                </div>
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleExportCSV}
                  disabled={filteredResponses.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DocumentArrowDownIcon className="h-5 w-5" />
                  CSV Export
                </button>
              </div>
            </div>

            {/* Results Table */}
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Laden...</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                          Schüler
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Empfänger
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Beantwortet
                        </th>
                        {questions.map((question) => (
                          <th
                            key={question.id}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]"
                          >
                            <div className="flex flex-col gap-1">
                              <span>{question.frage_text}</span>
                              <span className="text-xs font-normal text-gray-400 capitalize">
                                ({question.frage_typ === 'single' ? 'Einfachauswahl' :
                                  question.frage_typ === 'multiple' ? 'Mehrfachauswahl' :
                                  'Freitext'})
                              </span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredResponses.length === 0 ? (
                        <tr>
                          <td colSpan={3 + questions.length} className="px-6 py-8 text-center text-gray-500">
                            Keine Antworten gefunden
                          </td>
                        </tr>
                      ) : (
                        filteredResponses.map((recipient) => (
                          <tr key={recipient.empfaenger_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10">
                              <div>
                                <div className="font-medium text-gray-900">
                                  {recipient.schueler_vorname} {recipient.schueler_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  Klasse {recipient.schueler_klasse}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {recipient.empfaenger_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {recipient.empfaenger_email}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(recipient.beantwortet_am).toLocaleDateString('de-DE')}
                              <div className="text-xs text-gray-500">
                                {new Date(recipient.beantwortet_am).toLocaleTimeString('de-DE')}
                              </div>
                            </td>
                            {questions.map((question) => {
                              const answerText = getAnswerText(recipient, question);
                              return (
                                <td key={question.id} className="px-6 py-4 text-sm text-gray-900">
                                  {question.frage_typ === 'freitext' ? (
                                    <div className="max-w-xs">
                                      <p className="line-clamp-3 whitespace-pre-wrap">
                                        {answerText}
                                      </p>
                                      {answerText.length > 100 && (
                                        <button
                                          onClick={() => {
                                            // Show full text in alert for now
                                            alert(answerText);
                                          }}
                                          className="text-blue-600 hover:text-blue-800 text-xs mt-1"
                                        >
                                          Mehr anzeigen
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="flex flex-wrap gap-1">
                                      {answerText.split(', ').map((option, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                        >
                                          {option}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Summary */}
            {filteredResponses.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 text-blue-700">
                  <UserGroupIcon className="h-5 w-5" />
                  <span className="font-medium">
                    {filteredResponses.length} Antworten werden angezeigt
                    {filterClass && ` (Klasse ${filterClass})`}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyResponseDetails;
